import { memo, useMemo } from "react";
import { Icon } from "@ospweb/sdk-ui";
import { cn, formatNumberWithZeroCount } from "utils";
import { motion } from "framer-motion";
import NextImage from "../NextImage";
import TokenPriceDisplay from "../TokenDetails/TokenPriceDisplay";
import { type FormattedPriceData } from "../../utils/priceFormatter";
import { PortfolioManage } from "../PortfolioManage";

interface BitcoinPriceDisplayProps {
  // 必需参数
  formattedData: FormattedPriceData;
  tokenData: {
    icon?: string;
    name?: string;
    symbol?: string;
    id?: string;
    type?: string;
  };
  isPollToken?: boolean;
}

const BitcoinPriceDisplay = ({
  formattedData,
  tokenData,
  isPollToken,
}: BitcoinPriceDisplayProps) => {
  // 直接使用传入的格式化数据
  const displayData = formattedData;
  const { icon, name, symbol, id, type } = tokenData;
  const subTitle = symbol ? `${symbol} Price` : "";

  // 从 displayPrice 中提取数字字符串
  const numStr = useMemo(() => {
    if (!displayData.displayPrice) {
      return undefined;
    }
    // 移除 $ 符号并获取数字字符串
    return `${displayData.displayPrice}`.replace("$", "");
  }, [displayData.displayPrice]);

  // 缓存价格计算结果
  const priceData = useMemo(() => {
    return formatNumberWithZeroCount({
      numStr,
      decimals: 6,
      minZeroCount: 6,
    });
  }, [numStr]);

  // 缓存左侧数字计算结果
  const leftDigits = useMemo(() => {
    if (!priceData?.isSuccess) {
      return priceData?.leftDigits;
    }

    const integerPart = Number(priceData.integerPart);
    if (integerPart > 0 && priceData?.leftDigits?.length > 2) {
      return priceData.leftDigits?.substring(0, 2);
    } else if (integerPart === 0 && priceData?.leftDigits?.length > 6) {
      return Number(priceData.leftDigits?.substring(0, 6) || 0) !== 0
        ? priceData.leftDigits?.substring(0, 6)
        : null;
    }
    return priceData?.leftDigits;
  }, [priceData]);
  return (
    <div className="flex items-center gap-2">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{
          opacity: 0,
          transition: { duration: 0 },
        }}
        transition={{
          delay: 0.3,
          duration: 0,
        }}
        className={cn(
          "md:relative md:ml-[18px]",
          "max-md:absolute max-md:right-[15px] max-md:xs_button_icon max-md:xs_button max-md:tonal_button max-md:!w-7 max-md:!h-7 max-md:!rounded-[6px]",
          { hidden: isPollToken },
        )}
      >
        <PortfolioManage
          id={id || ""}
          type={type as "TOKEN" | "STOCK"}
          zIndex={999}
          className="flex"
        />
      </motion.div>
      {/* Bitcoin Logo */}
      <NextImage
        src={icon}
        className="w-8 h-8 rounded-full border border-border_solid"
      />
      {/* Price Information */}
      <div className="flex-1">
        <div className="flex items-center gap-0.5 mb-1">
          <span className="text-content_primary body_medium_m">{name}</span>
          {subTitle && (
            <span className="text-content_tertiary body_s">{subTitle}</span>
          )}
        </div>

        <div className="flex items-center gap-1">
          {priceData?.isSuccess ? (
            <TokenPriceDisplay
              integerPart={priceData.integerPart}
              leftDigits={leftDigits}
              zeroCount={priceData.zeroCount}
              className="text-content_primary body_medium_s"
              zeroCountClass="body_medium_xs"
            />
          ) : (
            "--"
          )}

          {displayData.displayChange && (
            <div
              className={`flex items-center gap-0.5 ${displayData.displayIsPositive ? "text-content_accent1_primary" : "text-content_accent2_primary"}`}
            >
              <Icon
                name={displayData.displayIsPositive ? "upvote" : "downvote"}
                size={8}
              />
              <span className="body_medium_s">
                {displayData.displayChange.replace(/[\+\｜\-]/g, "")}
              </span>
              {displayData.displayAbsoluteChange && (
                <span
                  className={`body_medium_s ml-0.5 ${
                    displayData.displayIsPositive
                      ? "text-content_accent1_primary"
                      : "text-content_accent2_primary"
                  }`}
                >
                  {displayData.displayAbsoluteChange.replace(/[\+\｜\-]/g, "")}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(BitcoinPriceDisplay);
